import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Stack,
    Chip,
    InputAdornment,
    TextField,
    Button,
    Snackbar,
    Alert,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from "react";
import { httpClient } from '../../../common/httpclient';
import PageScaffold from "../../../component/pageScaffold/PageScaffold";
import DefaultTable from "../../../component/table/DefaultTable";
import DialogForm from "../../../component/dialog/DialogForm";
import Dropdown from "../../../component/dropdown/Dropdown";
import { standaloneFieldSx } from "../../../common/fieldStyles";

// Opções do select "Tipo" (o valor vazio mantém o item "Nenhum")
const CONFIGURATION_TYPES = [
    { id: '', name: 'Nenhum' },
    { id: 'input', name: 'Texto' },
    { id: 'check', name: 'Checkbox' },
    { id: 'select', name: 'Seleção' },
    { id: 'file', name: 'Arquivo' },
];

export default function ConfigurationList() {
    const theme = useTheme();

    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [data, setData] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingConfiguration, setEditingConfiguration] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: '',
        type: '',
        parent: ''
    });
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleChangeSearch = (searchValue) => {
        setSearch(searchValue);

        if (!searchValue) {
            setFilteredData(data);
            return;
        }

        const searchLower = searchValue.toLowerCase();
        setFilteredData(
            data.filter(config =>
                config.name?.toLowerCase().includes(searchLower) ||
                config.description?.toLowerCase().includes(searchLower) ||
                config.category?.toLowerCase().includes(searchLower) ||
                config.type?.toLowerCase().includes(searchLower)
            )
        );
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await httpClient.get('/admin/configuration');
            const responseData = Array.isArray(response.data) ? response.data : [];
            setData(responseData);
            setFilteredData(responseData);
        } catch (error) {
            console.error('Error fetching configurations:', error);
            setData([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    const getTypeLabel = (type) => {
        const typeMap = {
            'input': 'Texto',
            'check': 'Checkbox',
            'select': 'Seleção',
            'file': 'Arquivo'
        };
        return typeMap[type] || type;
    };

    const getTypeColor = (type) => {
        const colorMap = {
            'input': 'primary',
            'check': 'success',
            'select': 'info',
            'file': 'warning'
        };
        return colorMap[type] || 'default';
    };

    const handleOpenModal = (configuration = null) => {
        if (configuration) {
            setEditingConfiguration(configuration);
            setFormData({
                name: configuration.name || '',
                description: configuration.description || '',
                category: configuration.category || '',
                type: configuration.type || '',
                parent: configuration.parent || ''
            });
        } else {
            setEditingConfiguration(null);
            setFormData({
                name: '',
                description: '',
                category: '',
                type: '',
                parent: ''
            });
        }
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        if (!saving) {
            setModalOpen(false);
            setEditingConfiguration(null);
            setFormData({
                name: '',
                description: '',
                category: '',
                type: '',
                parent: ''
            });
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!formData.name || !formData.description || !formData.category) {
            setSnackbar({
                open: true,
                message: 'Por favor, preencha todos os campos obrigatórios (Nome, Descrição e Categoria)',
                severity: 'error'
            });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: formData.name,
                description: formData.description,
                category: formData.category,
                ...(formData.type && { type: formData.type }),
                ...(formData.parent && { parent: formData.parent })
            };

            if (editingConfiguration) {
                // Atualizar configuração existente
                await httpClient.put(`/admin/configuration/${editingConfiguration.id}`, payload);
                setSnackbar({
                    open: true,
                    message: 'Configuração atualizada com sucesso!',
                    severity: 'success'
                });
            } else {
                // Criar nova configuração
                await httpClient.post('/admin/configuration', payload);
                setSnackbar({
                    open: true,
                    message: 'Configuração criada com sucesso!',
                    severity: 'success'
                });
            }

            setModalOpen(false);
            setEditingConfiguration(null);
            setFormData({
                name: '',
                description: '',
                category: '',
                type: '',
                parent: ''
            });
            
            // Recarregar a lista
            await fetchData();
        } catch (error) {
            console.error('Error saving configuration:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Erro ao salvar configuração',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const columns = [
        {
            name: 'Nome',
            path: 'name',
            style: { width: '20%', minWidth: '150px' },
            render: (row) => (
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        fontWeight: 500
                    }}
                    title={row.name}
                >
                    {row.name}
                </Box>
            )
        },
        {
            name: 'Descrição',
            path: 'description',
            style: { width: '35%', minWidth: '250px' },
            render: (row) => (
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        color: 'text.secondary'
                    }}
                    title={row.description || ''}
                >
                    {row.description || 'Sem descrição'}
                </Box>
            )
        },
        {
            name: 'Categoria',
            path: 'category',
            style: { width: '15%', minWidth: '120px' },
            render: (row) => (
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        color: 'text.secondary',
                        fontSize: '0.875rem'
                    }}
                    title={row.category || ''}
                >
                    {row.category || '-'}
                </Box>
            )
        },
        {
            name: 'Tipo',
            path: 'type',
            style: { width: '15%', minWidth: '120px' },
            render: (row) => (
                <Chip
                    label={getTypeLabel(row.type)}
                    color={getTypeColor(row.type)}
                    size="small"
                    variant="outlined"
                />
            )
        },
        {
            name: 'Pai',
            path: 'parent',
            style: { width: '15%', minWidth: '120px' },
            render: (row) => (
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        color: 'text.secondary',
                        fontSize: '0.875rem'
                    }}
                    title={row.parent || ''}
                >
                    {row.parent || '-'}
                </Box>
            )
        }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <PageScaffold links={[]} titlePageTarget={'Configurações do Sistema'}>
            {/* Seção de Filtros Moderna */}
            <Card
                sx={{
                    m: 0,
                    borderRadius: 3,
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(0,0,0,0.3)'
                        : '0 4px 20px rgba(0,0,0,0.08)',
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                        : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.1)'
                        : '1px solid rgba(0,0,0,0.05)'
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    {/* Header dos Filtros */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 3,
                        gap: 1
                    }}>
                        <FilterListIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: '1.25rem'
                        }}>
                            Filtros de Configurações
                        </Typography>
                    </Box>

                    <Box sx={{ width: '100%' }}>
                        {/* Campo de Busca */}
                        <Box sx={{ width: '100%' }}>
                            <TextField
                                id="search-input"
                                placeholder="Pesquisar por nome, descrição, categoria ou tipo..."
                                value={search}
                                onChange={(e) => handleChangeSearch(e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: 'primary.main' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'transparent',
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : 'rgba(0,0,0,0.1)',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme.palette.text.primary,
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Ações Rápidas */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 3,
                        pt: 2,
                        borderTop: theme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.08)'
                            : '1px solid rgba(0,0,0,0.08)'
                    }}>
                        <Stack direction="row" spacing={1}>
                            {search && (
                                <Chip
                                    label={`Busca: "${search}"`}
                                    onDelete={() => handleChangeSearch('')}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : undefined,
                                        color: theme.palette.mode === 'dark'
                                            ? 'primary.light'
                                            : undefined,
                                    }}
                                />
                            )}
                            <Chip
                                label={`${filteredData.length} configuração(ões) encontrada(s)`}
                                color="info"
                                variant="outlined"
                                size="small"
                                sx={{
                                    borderColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.2)'
                                        : undefined,
                                    color: theme.palette.mode === 'dark'
                                        ? 'info.light'
                                        : undefined,
                                }}
                            />
                        </Stack>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenModal()}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3,
                                boxShadow: theme.palette.mode === 'dark'
                                    ? '0 4px 12px rgba(25,118,210,0.3)'
                                    : '0 4px 12px rgba(25,118,210,0.2)',
                                '&:hover': {
                                    boxShadow: theme.palette.mode === 'dark'
                                        ? '0 6px 16px rgba(25,118,210,0.4)'
                                        : '0 6px 16px rgba(25,118,210,0.3)',
                                }
                            }}
                        >
                            Adicionar Configuração
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Box sx={{ width: '100%', mt: 2 }}>
                <DefaultTable
                    colunms={columns}
                    data={filteredData}
                    iconsActions={[
                        {
                            index: 0,
                            icon: <EditIcon />,
                            props: {
                                onClick: (row) => handleOpenModal(row),
                                tooltip: 'Editar'
                            }
                        }
                    ]}
                    loading={loading}
                    sortableFields={['name', 'description', 'category', 'type']}
                    defaultSort={{ field: 'name', direction: 'asc' }}
                />
            </Box>

            {/* Modal de Adicionar/Editar */}
            <DialogForm
                open={modalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                title={editingConfiguration ? 'Editar Configuração' : 'Adicionar Configuração'}
                subtitle={editingConfiguration ? 'Altere os dados da configuração' : 'Preencha os dados para criar uma nova configuração'}
                loading={saving}
                loadingText="Salvando..."
                confirmButtonText="Salvar"
                cancelButtonText="Cancelar"
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                    <TextField
                        label="Nome"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        fullWidth
                        required
                        variant="outlined"
                        disabled={saving}
                        sx={standaloneFieldSx}
                    />
                    <TextField
                        label="Descrição"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        fullWidth
                        required
                        variant="outlined"
                        multiline
                        rows={3}
                        disabled={saving}
                        sx={standaloneFieldSx}
                    />
                    <TextField
                        label="Categoria"
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        fullWidth
                        required
                        variant="outlined"
                        disabled={saving}
                        sx={standaloneFieldSx}
                    />
                    {/* Select de rótulo estático (`.ffs`) — não flutua */}
                    <Dropdown
                        label="Tipo"
                        labelVariant="static-micro"
                        data={CONFIGURATION_TYPES}
                        selectedItem={formData.type}
                        handleChange={(value) => handleInputChange('type', value)}
                        disabled={saving}
                        variant="outlined"
                        size="medium"
                        sx={standaloneFieldSx}
                    />
                    <TextField
                        label="Pai"
                        value={formData.parent}
                        onChange={(e) => handleInputChange('parent', e.target.value)}
                        fullWidth
                        variant="outlined"
                        disabled={saving}
                        helperText="Nome da configuração pai (opcional)"
                        sx={standaloneFieldSx}
                    />
                </Box>
            </DialogForm>

            {/* Snackbar para feedback */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </PageScaffold>
    );
}

